import type { RouteRecordRaw, Router } from 'vue-router'
import {
  marketingRoutesForServiceDesk,
  marketingRoutesForCommerce
} from './marketing'
import { getClusterProductStatus } from '../utils/cluster-products'

/**
 * Dynamically builds and returns fallback routes based on
 * product access and role-based permissions.
 *
 * This function also removes restricted MFE routes from the router
 * to prevent unauthorized navigation.
 *
 * @param userStore - Store instance containing access & role checks
 * @param router - Vue Router instance
 * @returns RouteRecordRaw[] - Routes to be dynamically added
 *
 * @author Harvinder Singh
 * @created 2026
 */
function getDynamicRoutes(
  userStore: any,
  router: Router
): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []

  /**
   * Helper to check both product access and role access
   */
  const hasFullAccess = (product: string): boolean =>
    userStore.getProductAccess(product) &&
    userStore.getProductAccessOnRoles(product)

  /**
   * Helper to safely remove a route if it exists
   */
  const removeRouteIfExists = (name: string) => {
    if (router.hasRoute(name)) {
      router.removeRoute(name)
    }
  }

  /* ------------------------------------------------------------------
   * MARKETING ACCESS HANDLING
   * ------------------------------------------------------------------ */
  if (!hasFullAccess('marketing')) {
    removeRouteIfExists('AccountsMFE')
    const { exists: existsMarketing, status: statusMarketing } = getClusterProductStatus('Marketing');
    if(existsMarketing){
      // Allow limited marketing routes via other products
      if (hasFullAccess('service')) {
        routes.push(...marketingRoutesForServiceDesk)
      }

      if (hasFullAccess('commerce')) {
        routes.push(...marketingRoutesForCommerce)
      }

      // Catch-all redirect for Accounts MFE
      routes.push({
        path: '/accounts/:pathMatch(.*)*',
        name: 'AccountsMFE',
        redirect: statusMarketing === "inactive" ? '/coming-soon/Marketing' : userStore.getProductAccess('marketing') && !userStore.getProductAccessOnRoles('marketing')
          ? '/access-denied'
          : '/preview'
      })
    }
  }

  /* ------------------------------------------------------------------
   * SERVICE ACCESS HANDLING
   * ------------------------------------------------------------------ */
  if (!hasFullAccess('service')) {
    removeRouteIfExists('ServiceMFE')

    const { exists: existsService, status: statusService } = getClusterProductStatus('Service');
    if(existsService){
      routes.push({
        path: '/service/:pathMatch(.*)*',
        name: 'ServiceMFE',
        redirect: statusService === "inactive" ? '/coming-soon/Service' : userStore.getProductAccess('service') && !userStore.getProductAccessOnRoles('service')
          ? '/access-denied'
          : '/preview'
      })
    }
  }

  /* ------------------------------------------------------------------
   * COMMERCE ACCESS HANDLING
   * ------------------------------------------------------------------ */
  if (!hasFullAccess('commerce')) {
    removeRouteIfExists('CommerceMFE')
    const { exists: existsCommerce, status: statusCommerce } = getClusterProductStatus('Commerce');
    if(existsCommerce){
    routes.push({
      path: '/commerce/:pathMatch(.*)*',
      name: 'CommerceMFE',
        redirect: statusCommerce === "inactive" ? '/coming-soon/Commerce' : '/preview'
      })
    }
  }

  return routes
}

export default getDynamicRoutes